import createClient from '../../../plugins/gatsby-source-bs-shopify/create-client'
import { find, path, pipe } from 'ramda'
import {
  Maybe,
  SignInUserInputType,
  AccessTokenType,
  CustomerType,
  SignUpUserInputType,
  CustomerAddressInputType,
  CustomerAddressType,
  CustomerInputType,
} from '~/@types/models'
import {
  signInMutation,
  signUpMutation,
  createAddressMutation,
  customerRecoverMutation,
  deleteAddressMutation,
  updateCustomerAddressMutation,
  updateCustomerMutation,
  updateDefaultAddressMutation,
  updatePasswordMutation,
} from './mutations'
import { customerQuery } from './queries'
import { checkoutProxy, CheckoutProxyType } from './checkout'
import { GraphQLClient } from 'graphql-request'

const store = process.env.GATSBY_SHOPIFY_SHOP_NAME
const accessToken = process.env.GATSBY_SHOPIFY_SF_ACCESS_TOKEN

enum ShopifyErrorCodeType {
  UNIDENTIFIED_CUSTOMER = 'UNIDENTIFIED_CUSTOMER',
  EMAIL_TAKEN = 'TAKEN',
  INVALID = 'INVALID',
}

type ClientProxyType = {
  signIn: (input: SignInUserInputType) => Promise<AccessTokenType>
  sinUp: (input: SignUpUserInputType) => Promise<CustomerType>
  fetchCustomer: (accessToken: string) => Promise<CustomerType>
  createAddress: (authToken: string, address: CustomerAddressInputType) => Promise<void>
  updateDefaultAddress: (authToken: string, addressId: string) => Promise<void>
  updatePassword: (password: string) => Promise<void>
  recoverPassword: (authToken: string, email: string) => Promise<void>
  deleteAddress: (authToken: string, id: string) => Promise<string>
  updateCustomer: (authToken: string, customer: CustomerInputType) => Promise<void>
  updateAddress: (authToken: string, addressId: string, address: CustomerInputType) => Promise<void>
  checkout: () => CheckoutProxyType
  getClient: () => GraphQLClient
}

const getErrorCode = (mutationName: string): Maybe<string> =>
  pipe(
    path([mutationName, 'customerUserErrors']),
    find(path(['code'])),
    path<string>(['code'])
  )

const performRequest = async <T>(
  client: GraphQLClient,
  mutation: string,
  variables = {},
  responsePath: string[]
): Promise<T> => {
  let response = null

  try {
    response = await client.request(mutation, variables)
  } catch (e) {
    throw new Error(`Something went wrong. Try again later please`)
  }

  const mutationName = responsePath[0]
  const hasError = Boolean(path([mutationName, 'customerUserErrors'], response))

  let errorCode = null

  if (hasError) {
    errorCode = getErrorCode(mutationName)(response)
  }

  if (errorCode === ShopifyErrorCodeType.UNIDENTIFIED_CUSTOMER) {
    throw new Error('Email or password is not valid')
  }

  if (errorCode === ShopifyErrorCodeType.EMAIL_TAKEN) {
    throw new Error(
      `This email address is already associated with an account.
      If this account is yours, you can reset your password.`
    )
  }

  if (errorCode === ShopifyErrorCodeType.INVALID) {
    throw new Error('Invalid values')
  }

  // now we are sure that the T response is present
  return path<T>(responsePath, response) as T
}

const client = createClient(store, accessToken)

export const clientProxyFactory = (): ClientProxyType => {
  const signIn = async (input: SignInUserInputType) => {
    return await performRequest<AccessTokenType>(client, signInMutation, { input }, [
      'customerAccessTokenCreate',
      'customerAccessToken',
    ])
  }

  const sinUp = async (input: SignUpUserInputType) => {
    return await performRequest<CustomerType>(client, signUpMutation, { input }, [
      'customerCreate',
      'customer',
    ])
  }

  const fetchCustomer = async (authToken: string) => {
    return await performRequest<CustomerType>(client, customerQuery, { accessToken: authToken }, [
      'customer',
    ])
  }

  const createAddress = async (authToken: string, address: CustomerAddressInputType) => {
    return await performRequest<CustomerAddressType>(
      client,
      createAddressMutation,
      { address, customerAccessToken: authToken },
      ['customerAddressCreate', 'customerAddress']
    )
  }

  const updateDefaultAddress = async (authToken: string, id: string) => {
    return await performRequest<CustomerAddressType>(
      client,
      updateDefaultAddressMutation,
      { addressId: id, customerAccessToken: authToken },
      ['customerAddressCreate', 'customerAddress']
    )
  }

  const updatePassword = async (authToken: string, password: string) => {
    return await performRequest<CustomerAddressType>(
      client,
      updatePasswordMutation,
      { customer: { password }, customerAccessToken: authToken },
      ['customerUpdate', 'customer']
    )
  }

  const updateCustomer = async (authToken: string, customer: CustomerInputType) => {
    return await performRequest<CustomerAddressType>(
      client,
      updateCustomerMutation,
      { customer, customerAccessToken: authToken },
      ['customerUpdate']
    )
  }

  const recoverPassword = async (authToken: string, email: string) => {
    return await performRequest<void>(client, customerRecoverMutation, { email }, [
      'customerRecover',
    ])
  }

  const deleteAddress = async (authToken: string, id: string) => {
    return await performRequest<void>(
      client,
      deleteAddressMutation,
      { id, customerAccessToken: authToken },
      ['customerAddressDelete', 'deletedCustomerAddressId']
    )
  }

  const updateAddress = async (
    authToken: string,
    id: string,
    address: CustomerAddressInputType
  ) => {
    return await performRequest<void>(
      client,
      updateCustomerAddressMutation,
      { address, id, customerAccessToken: authToken },
      ['customerAddressUpdate', 'customerAddress']
    )
  }

  return {
    signIn,
    sinUp,
    fetchCustomer,
    createAddress,
    updateDefaultAddress,
    updatePassword,
    recoverPassword,
    deleteAddress,
    updateCustomer,
    updateAddress,
    checkout: checkoutProxy,
    getClient: () => client,
  }
}
